// What a connection's deliveries say about it, in plain words (#579).
//
// On 2026-09-17 three Jira deliveries were abandoned — Jira answered `400 issuetype…`
// and then `401 You do not have permission…` — and the console still showed the
// connection as active with a saved token. The reason was on the delivery rows, and it
// took a new worker job and a deploy to read. This is that reading, on the settings
// surface, in words a person can act on.
//
// **Pure, and separate from the panel that renders it.** The health of a connection is
// decided here from its newest delivery and nowhere else, so the same rule reaches the
// row's chip and its expanded list; and a form that edits a connection (#580) can say
// what is wrong with it without reaching into the table.
//
// **The mapping is the console's, not the API's.** The route answers the adapter's own
// recorded text, whole; these are the five answers #265 and #266 actually hit, turned
// into the repair. An answer nothing here names is shown as the adapter wrote it —
// swallowing it would leave a person with "Failing" and nothing to do about it.

import type { CheckRow, DeliveryRow, IntegrationKind, IntegrationRow } from "../api/integrations";

/**
 * What a connection's last delivery says about it.
 *
 * `waiting` is the state #577 made necessary and is the one worth reading twice: a
 * delivery that is waiting its turn is `deferred`, which writes no `last_detail` and
 * spends no attempt, so on the row it is indistinguishable from one just recorded.
 * Both are genuinely waiting to be sent. Showing either as a failure would accuse the
 * connection of something it has not done; showing either as "Not used yet" would hide
 * a send that is about to happen. `unused` is kept for what it actually means — no
 * delivery row exists at all.
 */
export type Health = "working" | "failing" | "waiting" | "nothing-sent" | "unused";

/** The chip each health wears, as a person reads it. */
export const HEALTH_LABEL: Record<Health, string> = {
  working: "Working",
  failing: "Failing",
  waiting: "Waiting to send",
  "nothing-sent": "Nothing sent",
  unused: "Not used yet",
};

/** The chip's tone. A connection that is merely waiting is not yet a problem. */
export const HEALTH_TONE: Record<Health, "good" | "bad" | "warn" | undefined> = {
  working: "good",
  failing: "bad",
  waiting: undefined,
  "nothing-sent": undefined,
  unused: undefined,
};

/**
 * The check on save's answer, when it is the newest thing known about the connection (#598).
 *
 * A connection is checked the moment it is saved, so it has something to say before any
 * delivery has run. Whichever of the check and the newest delivery is later is the
 * connection's current state: a delivery that went through after a failed check has
 * answered the check's question, and a check after an edit is about the credential the
 * row holds now. On a tie the delivery wins — it is the real thing.
 */
export function newestCheck(row: IntegrationRow): CheckRow | null {
  const check = row.last_check ?? null;
  if (check === null) return null;
  const last = row.last_delivery;
  if (last === null || last === undefined) return check;
  return Date.parse(check.checked_at) > Date.parse(last.updated_at) ? check : null;
}

/** The health of one connection, from the newer of its check on save and its newest delivery. */
export function healthOf(row: IntegrationRow): Health {
  const check = newestCheck(row);
  if (check !== null) return check.status === "passed" ? "working" : "failing";
  return healthOfDelivery(row.last_delivery);
}

export function healthOfDelivery(last: DeliveryRow | null | undefined): Health {
  if (last === null || last === undefined) return "unused";
  switch (last.status) {
    case "delivered":
      return "working";
    case "abandoned":
    case "failed":
      return "failing";
    case "skipped":
      return "nothing-sent";
    default:
      return "waiting";
  }
}

/** Nothing has been attempted on this row: `pending` writes no detail and spends no attempt. */
const UNATTEMPTED = "Queued. It goes out on the next pass.";

/**
 * The newest check went through, and nothing has been delivered since. True whether a save
 * or Test connection asked it: `last_check` does not say which (#615).
 */
const CHECK_PASSED = "Checked, and the connection answered. Nothing has been sent since.";

/**
 * The token answered, and Slack would not say whether the app is in the channel: the app
 * was installed without `channels:read`. Not a failure — it can still post — and not
 * a clean pass either, so it says which half was checked.
 */
const CHECK_PASSED_CHANNEL_UNREAD =
  "Checked: Slack accepted the token. The channel could not be checked, because the app was installed without channels:read.";

/** A test delivery went through (#614): something *was* sent, and for Jira someone has to close it. */
const TEST_ACCEPTED: Record<IntegrationKind, string> = {
  jira: "A test work item was sent, and Jira accepted it. Close it in Jira once you have seen it.",
  slack: "A test message was sent, and Slack accepted it.",
  webhook: "A test event was sent, and the receiver accepted it.",
};

/** Nothing was sent on purpose — below the channel's severity floor, or no card to answer. */
const NOTHING_TO_SEND = "Nothing was sent for this event, on purpose.";

/** A delivery that went through (#798): what a delivered row says, whatever detail it carries. */
const DELIVERED: Record<IntegrationKind, string> = {
  jira: "Delivered. Jira accepted it.",
  slack: "Delivered. Slack accepted it.",
  webhook: "Delivered. The receiver accepted it.",
};

/**
 * What one delivery row says, whatever its status (#798).
 *
 * The expanded list reads every row, delivered ones included, so it cannot read them
 * through `failureInWords` alone: a delivered row with no detail read there as a refusal.
 */
export function deliveryInWords(
  kind: IntegrationKind,
  delivery: Pick<DeliveryRow, "status" | "last_status" | "last_detail">,
): string {
  switch (delivery.status) {
    case "delivered":
      return DELIVERED[kind];
    case "pending":
    case "skipped":
    case "failed":
    case "abandoned":
      return failureInWords(kind, delivery);
    default: {
      const unknown: never = delivery.status;
      return `Status: ${String(unknown).replace(/_/g, " ")}.`;
    }
  }
}

/**
 * The one status a connection shows (#798), from whether it is connected and how it is.
 *
 * A disconnected connection is only that; a connected one is its health, which already
 * says whether it works. "Connected" beside "Failing" was two answers to one question.
 */
export function statusOf(row: IntegrationRow): { label: string; tone: "good" | "bad" | "warn" | undefined } {
  if (!row.active) return { label: "Disconnected", tone: undefined };
  if (removedFromSlack(row)) return { label: "Removed from Slack", tone: "bad" };
  const health = healthOf(row);
  return { label: HEALTH_LABEL[health], tone: HEALTH_TONE[health] };
}

/** A connection that posts with the workspace's installation (#936), which holds no token of its own. */
const fromInstallation = (row: IntegrationRow): boolean => row.credential_kind === "installation";

/**
 * Slack removed the installation the connection posts through (#938): uninstalled from the
 * workspace, so nothing is sent until it is reinstalled. Read from the installation, not
 * from a delivery: a connection with nothing to send yet has been removed all the same.
 */
export const removedFromSlack = (row: IntegrationRow): boolean => (row.installation_removed_at ?? null) !== null;

/** What a connection whose installation Slack removed says, and the one thing that resumes it (#938). */
const REMOVED_FROM_SLACK = "Removed from Slack — reinstall to resume.";

/**
 * Why the last delivery failed, and what to do about it — or the adapter's own words.
 *
 * The five mapped answers are the ones the live proofs of #265 and #266 hit. Everything
 * else falls through to `last_detail` exactly as the adapter recorded it.
 */
export function failureInWords(
  kind: IntegrationKind,
  delivery: Pick<DeliveryRow, "status" | "last_status" | "last_detail">,
): string {
  const said = (delivery.last_detail ?? "").trim();
  if (delivery.status === "pending") return UNATTEMPTED;
  if (delivery.status === "skipped") return said === "" ? NOTHING_TO_SEND : said;
  const mapped = mappedFailure(kind, delivery.last_status ?? null, said);
  if (mapped !== null) return mapped;
  if (said !== "") return said;
  return "The connection refused this delivery and gave no reason.";
}

function mappedFailure(kind: IntegrationKind, status: number | null, said: string): string | null {
  if (kind === "jira") {
    if (status === 401 || status === 403) {
      return "Jira rejected the email and token pair. Re-enter the Atlassian API token for that account.";
    }
    // A refused create answers 400; the check on save reads the space's own list and has
    // no status to give (#598). Both name `issuetype`, and both mean this.
    if ((status === 400 || status === null) && /\bissuetype\b/i.test(said)) {
      return "That work type doesn't exist in the space. Change the work type to one the space has.";
    }
    if (status === 400 && /\blabels\b/i.test(said)) {
      return "Labels can't contain spaces. Use single words, separated by commas.";
    }
    return null;
  }
  // A receiver's refusal is its own words: none of Slack's codes is ever one (#1073).
  if (kind === "webhook") return null;
  if (/\bnot_in_channel\b/.test(said)) {
    return "Invite the app to the channel: type /invite @your-app in Slack.";
  }
  if (/\b(invalid_auth|token_revoked|account_inactive)\b/.test(said)) {
    return "The Slack token is no longer valid. Create a new Bot User OAuth Token and re-enter it.";
  }
  if (/\bchannel_not_found\b/.test(said)) {
    return "Slack does not know that channel ID. Check it in Slack: the channel → its name → About → Channel ID.";
  }
  return null;
}

/**
 * A check Slack answered without `channels:read` (#598, #605): by the scope Slack named
 * (`slack.classify` keeps its `needed`), or by the one missing scope a check recorded
 * before any was named — `conversations.info` on a public channel, whose scope is this one.
 */
const NEEDS_CHANNELS_READ = /\bmissing_scope \(needed: [^)]*\bchannels:read\b|\(conversations\.info: missing_scope\)$/;

/** A passed check that could not read the channel: the app holds no `channels:read` (#598). */
export const channelWentUnread = (check: CheckRow): boolean => NEEDS_CHANNELS_READ.test(check.last_detail);

/** A passed check that could not read the channel for some other missing scope: shown as Slack said it. */
const otherScopeMissing = (check: CheckRow): boolean => /\bmissing_scope\b/.test(check.last_detail) && !channelWentUnread(check);

/**
 * The permission a Slack connection was installed without, or null (#605).
 *
 * Read off `last_check` whatever delivery came after it, because only a check that reads
 * the channel can say — a delivery or a test message is `chat:write` and says nothing
 * about `channels:read`. The finding is `last_check.needs`, which the server keeps beside
 * whatever answer holds `last_check` now, a test message's included, and which only such
 * a check sets or clears (`integrations/check.py`, `asked_and_kept`). A row checked
 * before `needs` was kept is read by its check's own words. Never a failure: the
 * connection posts, and only the channel list needs it.
 */
export function missingGrant(row: IntegrationRow): "channels:read" | null {
  // An installation's grant is what Slack answered the install with (#938): read from there,
  // so the reinstall that adds it clears this at once, whatever an older check found.
  if (Array.isArray(row.installation_scopes)) {
    return row.installation_scopes.includes("channels:read") ? null : "channels:read";
  }
  const check = row.last_check ?? null;
  if (row.kind !== "slack" || check === null) return null;
  if (check.needs !== undefined) return check.needs.split(",").includes("channels:read") ? "channels:read" : null;
  if (check.what !== undefined || check.status !== "passed") return null;
  return channelWentUnread(check) ? "channels:read" : null;
}

/**
 * What a connection without `channels:read` says under its health, and what to do (#605).
 * A pasted token's app is repaired from Edit; an installation by Reinstall, which asks Slack
 * for every scope an install asks for today (#938, #581 Q2).
 */
export function grantInWords(row: IntegrationRow, mayEdit: boolean): string {
  if (fromInstallation(row)) {
    const granted = "Slack has not given Torque Assist the channels:read permission, so its channels cannot be listed. Posting is unaffected.";
    return mayEdit
      ? `${granted} To add it, choose Reinstall: Slack asks for every permission Torque Assist uses.`
      : `${granted} A workspace admin can reinstall it.`;
  }
  const said = "Slack has not given this app the channels:read permission, so its channels cannot be listed. Posting is unaffected.";
  return mayEdit ? `${said} To add it, choose Edit and follow the steps there.` : `${said} A workspace admin can add it.`;
}

/**
 * What a test delivery's answer says (#614, #1076): what the health line says of an accepted
 * one, and a refused one in the words a failed delivery is read in.
 */
export function testDeliveryInWords(kind: IntegrationKind, check: CheckRow): string {
  return check.status === "passed" ? TEST_ACCEPTED[kind] : failedCheckInWords(kind, check);
}

/** A failed check in the words a failed delivery is read in: one mapping for both (#598, #603). */
export function failedCheckInWords(kind: IntegrationKind, check: CheckRow): string {
  // A delivery's two fields under a delivery's two names.
  return failureInWords(kind, { status: "failed", last_status: check.last_status, last_detail: check.last_detail });
}

/**
 * The one line under a connection's chip, whatever its health. Never empty: a person
 * looking at a row should not have to expand it to learn whether anything is wrong.
 */
export function healthInWords(row: IntegrationRow): string {
  if (removedFromSlack(row)) return REMOVED_FROM_SLACK;
  const check = newestCheck(row);
  if (check !== null) {
    if (check.status === "passed") {
      if (check.what === "test_delivery") return TEST_ACCEPTED[row.kind];
      if (otherScopeMissing(check)) return check.last_detail;
      return channelWentUnread(check) ? CHECK_PASSED_CHANNEL_UNREAD : CHECK_PASSED;
    }
    return failedCheckInWords(row.kind, check);
  }
  const last = row.last_delivery;
  if (last === null) return "Nothing has been sent to this connection yet.";
  switch (healthOfDelivery(last)) {
    case "working":
      return `Last ${last.event ?? "delivery"} went through.`;
    case "waiting":
      return UNATTEMPTED;
    default:
      return failureInWords(row.kind, last);
  }
}

/** Where a delivery landed on the far side, when it landed anywhere a person can open. */
export function deliveryLink(kind: IntegrationKind, delivery: DeliveryRow): { href: string; text: string } | null {
  // A receiver's delivery lands at an address of the customer's, with nothing to open (#1073).
  if (kind === "webhook") return null;
  if (kind === "jira") {
    const url = delivery.ticket?.url;
    if (typeof url !== "string" || url === "") return null;
    return { href: url, text: delivery.ticket?.key ?? "the work item" };
  }
  const slack = delivery.slack;
  if (slack === undefined || typeof slack.channel !== "string" || typeof slack.ts !== "string") {
    return null;
  }
  return { href: slackPermalink(slack.channel, slack.ts), text: "the message" };
}

/** Where a person opens one Slack message: the deliveries list's link and the run page's (#818). */
export function slackPermalink(channel: string, ts: string): string {
  // Slack's own permalink shape: the message id is the timestamp with the dot dropped.
  return `https://slack.com/archives/${encodeURIComponent(channel)}/p${encodeURIComponent(ts.replace(".", ""))}`;
}
